import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import { fetchStats } from "./api";
import {
  agreement,
  canLog,
  fit,
  measuresFor,
  pairs,
  type Fit,
  type LevelAgreement,
  type Level,
  type Measure,
  type Point,
  type StatsTable,
} from "./stats";

const LEVELS: Level[] = ["departement", "commune", "iris"];

export interface CrossSection {
  isLoading: boolean;
  isError: boolean;
  table: StatsTable | null;
  /** Measures available at the active geography. */
  measures: Measure[];
  points: Point[];
  fit: Fit;
  /**
   * The log settings actually applied. A requested log scale is refused on an
   * axis carrying zero or negative values rather than dropping those zones.
   */
  logX: boolean;
  logY: boolean;
  /** True when a log scale was asked for and could not be given. */
  logXRefused: boolean;
  logYRefused: boolean;
  /** The same correlation at every geography, and whether they disagree. */
  agreement: LevelAgreement;
}

export interface CrossSectionQuery {
  level: Level;
  x: string;
  y: string;
  logX: boolean;
  logY: boolean;
  dep: string;
  minSales: number;
}

/**
 * The whole region, at all three geographies.
 *
 * All three are fetched rather than only the active one, because the panel's
 * most useful claim is whether the relationship survives changing the
 * geography, and that cannot be answered from one table. Together they are a
 * couple of megabytes, fetched once and never refetched.
 */
export function useCrossSection(q: CrossSectionQuery): CrossSection {
  const results = useQueries({
    queries: LEVELS.map((level) => ({
      queryKey: ["stats", level],
      queryFn: () => fetchStats(level),
      staleTime: Infinity,
      retry: false,
    })),
  });

  const tables = useMemo(() => {
    const out: Partial<Record<Level, StatsTable>> = {};
    results.forEach((r, i) => {
      if (r.data) out[LEVELS[i]] = r.data;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => (r.data ? 1 : 0)).join("")]);

  const table = tables[q.level] ?? null;

  const points = useMemo(() => {
    if (!table) return [];
    return pairs(table.zones, q.x, q.y, (z) => {
      if (q.dep !== "all" && z.dep !== q.dep) return false;
      if (q.minSales > 0) {
        const sales = z.sales;
        if (typeof sales !== "number" || sales < q.minSales) return false;
      }
      return true;
    });
  }, [table, q.x, q.y, q.dep, q.minSales]);

  const logX = q.logX && canLog(points.map((p) => p.x));
  const logY = q.logY && canLog(points.map((p) => p.y));

  const theFit = useMemo(() => fit(points, logX, logY), [points, logX, logY]);

  const theAgreement = useMemo(
    () => agreement(tables, q.x, q.y, logX, logY),
    [tables, q.x, q.y, logX, logY],
  );

  return {
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    table,
    measures: measuresFor(table),
    points,
    fit: theFit,
    logX,
    logY,
    logXRefused: q.logX && !logX,
    logYRefused: q.logY && !logY,
    agreement: theAgreement,
  };
}
